import React from 'react';
import {
  Plugins,
  withSystem,
  Atom,
  Expressions,
  type AtomRenderProps,
} from '@packages/systems/wfdl';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {SHARED_FORM_CONSTRAINTS_DESCENDANTS} from '@packages/systems/core/constants/SharedConstraintRules';
import {SHARED_INPUT_WRAPPER_CONSTRAINTS_DESCENDANTS} from '@packages/shared/constants/inputWrapperConstraints';
import {
  TText,
  TRecord,
  TTypeConstructor,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  findChild,
  getChildren,
  getInPojo,
} from '@packages/utilities/data-utils';
import {isFormRadioInput} from '@packages/utilities/elementTypeUtils';
import {RADIO_DEFAULT_VALUE} from './constants';
import {createDevlinkAtom} from '@packages/utilities/integrated-types';

const FormRadioWrapper = Atom.createRender(
  ({
    attributes,
    classes,
    generateAttributes,
    data,
    context,
    renderChild,
  }: AtomRenderProps) => {
    const wrapperClasses = classes.push('w-radio');
    const radioInputChild = findChild(data, isFormRadioInput);
    const radioInputElement = getInPojo(radioInputChild, [
      'props',
      'element',
    ]) as ExpressionElement | undefined;
    const id = radioInputElement
      ? Expressions.getElementDataAt(
          radioInputElement,
          [
            {in: 'Record', at: 'attr'},
            {in: 'Record', at: 'id'},
          ],
          'Text',
          RADIO_DEFAULT_VALUE
        )
      : RADIO_DEFAULT_VALUE;
    const updatedContext = context.set('for', id);

    return (
      // TODO: fix jsx-a11y lint error
      // eslint-disable-next-line jsx-a11y/label-has-for
      <label {...generateAttributes(attributes, wrapperClasses)}>
        {getChildren(data).map((child) => renderChild(child, updatedContext))}
      </label>
    );
  }
);

const config = createDevlinkAtom({
  constraints: {
    ancestors: [
      {
        is: 'FormForm',
        rule: 'ExactlyOne',
      },
    ],
    descendants: [
      ...SHARED_FORM_CONSTRAINTS_DESCENDANTS,
      ...SHARED_INPUT_WRAPPER_CONSTRAINTS_DESCENDANTS,
    ],
  },
  displayName: 'Radio Button Field',
  render: FormRadioWrapper,
  showEmpty: true,
  fields: {
    form: withSystem(
      {private: true, defaultValue: 'radio'},
      TRecord({type: TText})
    ),
    children: withSystem(
      {
        labelVariant: 'hidden',
      },
      TTypeConstructor(qualify(Plugins.name('Form'), typeName('RadioChildren')))
    ),
  },
  showSettingsInDescendants: true,
  // Add the native ID path in attributes for Analyze tracking
  autoEngagementTrackingAttribute: true,
});

export default config;
